/**
 * Structured logger — thin wrapper over `console.{info,warn,error,debug}`
 * that always tags entries with the contextual fields the triage runbook
 * (`docs/triage.md`) expects.
 *
 * Log shape (single-line JSON inside `console.{level}` so it survives
 * Outlook's WebView2 console + Application Insights ingestion equally
 * well):
 *
 *   { ts, level, msg, correlation_id, phase, instance_id, workflow_id,
 *     build_sha, auth_tier, ... }
 *
 * Build SHA comes from `import.meta.env.VITE_BUILD_SHA` injected by
 * vite.config.ts at build time. In jest there is no `import.meta.env` so
 * `safeBuildSha()` falls back to "dev". `auth_tier` is set per-token-
 * acquisition by useAuth.ts.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

export type AuthTier =
  | "office_sso"
  | "dialog_handoff"
  | "msal_silent"
  | "msal_popup"
  | "msal_redirect"
  | "stub";

export interface LogContext {
  correlation_id?: string;
  phase?: string;
  instance_id?: string;
  workflow_id?: string;
  build_sha?: string;
  auth_tier?: AuthTier;
  /** Free-form key/value extras — flattened into the log line. */
  [key: string]: unknown;
}

/**
 * Resolve the build SHA without crashing when `import.meta.env` is
 * unavailable (jest under ts-jest CommonJS target). The actual `import`
 * site is wrapped in a try/catch via `Function` to defer evaluation past
 * static analysis.
 */
function safeBuildSha(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta = (Function("return typeof import.meta !== 'undefined' ? import.meta : null")() as any);
    if (meta?.env?.VITE_BUILD_SHA) return String(meta.env.VITE_BUILD_SHA);
  } catch {
    // ignore
  }
  return "dev";
}

const BUILD_SHA = safeBuildSha();

let GLOBAL_CONTEXT: LogContext = {};

/** Merge fields into the global context that every subsequent log emits. */
export function setGlobalContext(patch: LogContext): void {
  GLOBAL_CONTEXT = { ...GLOBAL_CONTEXT, ...patch };
}

export function clearGlobalContext(): void {
  GLOBAL_CONTEXT = {};
}

function emit(level: LogLevel, msg: string, ctx?: LogContext): void {
  const payload = {
    ts: new Date().toISOString(),
    level,
    msg,
    build_sha: BUILD_SHA,
    ...GLOBAL_CONTEXT,
    ...(ctx ?? {}),
  };
  // eslint-disable-next-line no-console
  const fn =
    level === "error"
      ? console.error
      : level === "warn"
        ? console.warn
        : level === "debug"
          ? console.debug ?? console.log
          : console.info;
  // Single-line JSON so the entry survives multiline truncation in
  // WebView2's developer console (which clips at ~4 KB per console.X
  // call) and is greppable by correlation_id.
  try {
    fn.call(console, JSON.stringify(payload));
  } catch {
    // Circular references or BigInts — fall back to a safe stringify.
    fn.call(console, `[ora-log] ${level} ${msg}`);
  }
}

export const logger = {
  debug: (msg: string, ctx?: LogContext) => emit("debug", msg, ctx),
  info: (msg: string, ctx?: LogContext) => emit("info", msg, ctx),
  warn: (msg: string, ctx?: LogContext) => emit("warn", msg, ctx),
  error: (msg: string, ctx?: LogContext) => emit("error", msg, ctx),
};

/**
 * RFC 4122 v4 UUID using the Web Crypto API. Falls back to a
 * Math.random-seeded variant when `crypto.randomUUID` is not
 * available (jest-jsdom polyfill, very old hosts).
 */
export function newCorrelationId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  // Fallback v4-ish UUID.
  const bytes = new Array(16).fill(0).map(() => Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
